import { FC, Fragment, useState } from 'react';
import { Avatar, Button, Dropdown, Menu } from 'antd';
import { TbEdit, TbEye, TbMessageCircle, TbPlus, TbUsers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { teamService } from '@/services';
import { VirtualizedList as List } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { ResourceType } from '@/stores/database/entities/00-RBAC/99-Collaborators/types';
import {
    CollaboratorPermissionType,
    Team,
    TeamResourceAccessPrincipalType,
    TeamResourceAccessResourceType,
} from '@/types';

import type { InviteableWorkspaceResource } from '../../../../../../types';

interface TeamListItemProps {
    result: Team;
    resource: InviteableWorkspaceResource;
    resourceType: ResourceType;
    canInvite: boolean;
    setInput: (value: string) => void;
}

const resourceTypeMap: Record<ResourceType, TeamResourceAccessResourceType> = {
    folders: TeamResourceAccessResourceType.FOLDER,
    memos: TeamResourceAccessResourceType.MEMO,
    investments: TeamResourceAccessResourceType.INVESTMENT,
    pipelines: TeamResourceAccessResourceType.PIPELINE,
    dashboards: TeamResourceAccessResourceType.DASHBOARD,
    lists: TeamResourceAccessResourceType.LIST,
    lifecycles: TeamResourceAccessResourceType.LIFECYCLE,
};

const TeamListItem: FC<TeamListItemProps> = ({ canInvite, resource, resourceType, result, setInput }): JSX.Element => {
    const [permission, setPermission] = useState<CollaboratorPermissionType>(CollaboratorPermissionType.VIEW);
    const [inviting, setInviting] = useState(false);
    const { token } = useAntDesignTheme();

    const handleInviteTeamToResource = async () => {
        if (!canInvite || !resource?.id) {
            return;
        }

        setInviting(true);

        try {
            await teamService.grantWorkspaceResourceAccessToTeam({
                teamId: result.id,
                principalType: TeamResourceAccessPrincipalType.TEAM,
                resourceType: resourceTypeMap[resourceType],
                resourceId: resource.id,
                permission,
            });

            toast('Team Invited', {
                description: result.name,
            });
            setInput('');
        } finally {
            setInviting(false);
        }
    };

    return (
        <Fragment key={result.id}>
            <List.Item>
                <List.Item.Meta
                    avatar={
                        <Avatar
                            shape='square'
                            icon={
                                result?.emoji ? (
                                    `${result?.emoji}`
                                ) : (
                                    <TbUsers size='1rem' style={{ verticalAlign: '-0.125rem' }} />
                                )
                            }
                            style={{ verticalAlign: '-1.5rem' }}
                        />
                    }
                    title={result?.name}
                    description={`${result?.teammateCount ?? result?.teammates?.length ?? 0} Members`}
                />
                <Dropdown
                    disabled={!canInvite}
                    placement='bottomRight'
                    trigger={['click']}
                    popupRender={() => (
                        <DropdownMenuPanel
                            disabled={!canInvite}
                            selectedKeys={[permission]}
                            onClick={(e: any) => {
                                setPermission(e.key);
                            }}
                        >
                            <Menu.Item key={CollaboratorPermissionType.VIEW}>
                                <TbEye size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />
                                View
                            </Menu.Item>
                            <Menu.Item key={CollaboratorPermissionType.COMMENT}>
                                <TbMessageCircle
                                    size='1rem'
                                    style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }}
                                />
                                Comment
                            </Menu.Item>
                            <Menu.Item key={CollaboratorPermissionType.EDIT}>
                                <TbEdit size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />
                                Edit
                            </Menu.Item>
                        </DropdownMenuPanel>
                    )}
                >
                    <DropdownButton disabled={!canInvite} style={{ marginRight: '0.75rem' }} type='text'>
                        {permission}
                    </DropdownButton>
                </Dropdown>
                <Button
                    disabled={!canInvite}
                    loading={inviting}
                    type='dashed'
                    onClick={handleInviteTeamToResource}
                    icon={<TbPlus size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />}
                >
                    Invite
                </Button>
            </List.Item>
        </Fragment>
    );
};

export default TeamListItem;
